import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Calendar, User, ArrowLeft, Tag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Helmet } from 'react-helmet-async';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function BlogDetail() {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBlog();
  }, [slug]);

  const fetchBlog = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/blogs/${slug}`);
      setBlog(response.data);
    } catch (error) {
      console.error('Error fetching blog:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <p className="text-text-secondary text-xl" data-testid="blog-loading">Loading...</p>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-text-secondary text-xl mb-4">Blog not found</p>
          <Link to="/blogs">
            <Button className="btn-secondary">Back to Blogs</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20 min-h-screen" style={{backgroundColor: "#2C3540"}}>
      {blog && (
        <Helmet>
          <title>{blog.title} - Star Nivesh</title>
          <meta name="description" content={blog.meta_description || blog.excerpt} />
          <meta name="robots" content={blog.meta_robots || "index, follow"} />
          <meta property="og:title" content={blog.title} />
          <meta property="og:description" content={blog.meta_description || blog.excerpt} />
          <meta property="og:image" content={blog.image_url} />
          <meta property="og:type" content="article" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={blog.title} />
          <meta name="twitter:description" content={blog.meta_description || blog.excerpt} />
          <meta name="twitter:image" content={blog.image_url} />
        </Helmet>
      )}
      <article className="py-12" style={{backgroundColor: "#2C3540"}} data-testid="blog-detail">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/blogs">
            <Button variant="ghost" className="mb-8" data-testid="blog-back-button">
              <ArrowLeft className="mr-2 h-4 w-4" /> Back to Blogs
            </Button>
          </Link>

          <div className="glass-effect rounded-2xl overflow-hidden">
            <div className="relative h-96">
              <img
                src={blog.image_url}
                alt={blog.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent"></div>
            </div>

            <div className="p-8 md:p-12">
              <h1 className="font-heading text-4xl md:text-5xl font-bold text-text-primary mb-6" data-testid="blog-title">
                {blog.title}
              </h1>

              <div className="flex items-center space-x-6 text-text-muted mb-8 pb-8 border-b border-white/10">
                <span className="flex items-center">
                  <User className="h-5 w-5 mr-2" />
                  {blog.author}
                </span>
                <span className="flex items-center">
                  <Calendar className="h-5 w-5 mr-2" />
                  {new Date(blog.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
                </span>
              </div>

              {/* Tags */}
              {blog.tags && blog.tags.length > 0 && (
                <div className="flex items-center gap-2 mb-8 flex-wrap">
                  <Tag className="h-4 w-4 text-primary" />
                  {blog.tags.map((tag, index) => (
                    <span 
                      key={index}
                      className="px-3 py-1 bg-primary/20 text-primary rounded-full text-sm"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}

              <div className="blog-content prose prose-invert prose-lg max-w-none" data-testid="blog-content">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{blog.content}</ReactMarkdown>
              </div>
            </div>
          </div>

          <div className="mt-12 text-center glass-effect p-8 rounded-2xl">
            <h3 className="font-heading text-2xl font-bold text-text-primary mb-4">
              Ready to start your investment journey?
            </h3>
            <Link to="/contact">
              <Button className="btn-primary" data-testid="blog-cta-button">Get in Touch</Button>
            </Link>
          </div>
        </div>
      </article>
    </div>
  );
}
